import { graphPostJson } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { logConnectorFailure } from "../../../telemetry";
import { openInBrowser } from "./outlook";

/*
 * Creating a meeting from the pane. Nothing here sends anything: the invite
 * opens in Outlook, filled in, and the user checks it and sends it. For a
 * Teams meeting the join link is made first (as Chat's booking does) so it is
 * already in the invite.
 */

export interface NewMeetingDraft {
  readonly subject: string;
  readonly attendees: readonly string[];
  readonly start: Date;
  readonly end: Date;
  readonly location: string;
  /** For Outlook's own form, as HTML. */
  readonly bodyHtml: string;
  /** For Outlook on the web's compose page, which takes plain text. */
  readonly bodyText: string;
}

export const TEAMS_LOCATION = "Microsoft Teams Meeting";

/**
 * Put in the invite when the Teams link couldn't be made. Same wording as the
 * backend's _MANUAL_TOGGLE_BANNER (submit_teams_meeting_draft_tool.py), so
 * Chat and the pane say the same thing.
 */
export const TEAMS_TOGGLE_BANNER =
  "<p><strong>Microsoft Teams Meeting</strong> — toggle <em>Teams meeting</em> " +
  "in the form above to attach the join link before sending.</p><hr/>";
export const TEAMS_TOGGLE_TEXT = "Turn on Teams meeting before sending to add the join link.";

interface OnlineMeetingResponse {
  readonly joinUrl?: string | null;
  readonly joinWebUrl?: string | null;
  readonly joinInformation?: { readonly content?: string | null } | null;
}

const escapeAttr = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/**
 * Graph returns `joinInformation.content` as raw HTML or as a data: URI
 * (form-encoded or base64); Outlook's form needs raw HTML. Mirrors the
 * backend's _normalise_join_info. "" when it can't be decoded.
 */
function decodeJoinInformation(raw: string): string {
  if (!raw.startsWith("data:text/html")) return raw;
  const comma = raw.indexOf(",");
  const header = comma < 0 ? raw : raw.slice(0, comma);
  const payload = comma < 0 ? "" : raw.slice(comma + 1);
  if (!payload) return "";
  try {
    if (header.includes(";base64")) {
      const bytes = Uint8Array.from(atob(payload), (c) => c.codePointAt(0) ?? 0);
      return new TextDecoder().decode(bytes);
    }
    return decodeURIComponent(payload.replace(/\+/g, " "));
  } catch (e: unknown) {
    logConnectorFailure("calendar", e, "decode-teams-join-info");
    return "";
  }
}

export interface TeamsJoin {
  /** The join block for the invite body. */
  readonly html: string;
  readonly url: string;
}

/**
 * A Teams meeting for the invite, via `POST /me/onlineMeetings` (needs
 * OnlineMeetings.ReadWrite). This only makes the join link: nobody is invited
 * or told anything until the user sends the invite.
 */
export async function createTeamsJoin(
  token: string,
  refresh: RefreshTokenFn | undefined,
  subject: string,
  start: Date,
  end: Date,
): Promise<TeamsJoin> {
  const meeting = await graphPostJson<OnlineMeetingResponse>(
    "/me/onlineMeetings",
    { subject: subject || "Meeting", startDateTime: start.toISOString(), endDateTime: end.toISOString() },
    token,
    refresh,
  );
  const candidate = (meeting.joinUrl ?? meeting.joinWebUrl ?? "").trim();
  const url = candidate.startsWith("https://") ? candidate : "";
  const html =
    decodeJoinInformation((meeting.joinInformation?.content ?? "").trim()) ||
    (url ? `<p><strong>Microsoft Teams Meeting</strong></p><p><a href="${escapeAttr(url)}">Click here to join the meeting</a></p>` : "");
  if (!html || !url) throw new Error("The Teams meeting came back without join details");
  return { html, url };
}

/** Whether this Outlook can open its own new-meeting form from the pane. */
export function canOpenOutlookForm(): boolean {
  const mailbox = Office.context?.mailbox;
  return typeof mailbox?.displayNewAppointmentFormAsync === "function" || typeof mailbox?.displayNewAppointmentForm === "function";
}

/** Open Outlook's own new-meeting form, filled in. The user checks it and sends it. */
export function openOutlookForm(draft: NewMeetingDraft): Promise<void> {
  const mailbox = Office.context.mailbox;
  const params = {
    requiredAttendees: [...draft.attendees],
    subject: draft.subject,
    start: draft.start,
    end: draft.end,
    location: draft.location,
    body: draft.bodyHtml,
  };
  if (typeof mailbox.displayNewAppointmentFormAsync === "function") {
    return new Promise((resolve, reject) => {
      mailbox.displayNewAppointmentFormAsync(params, (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
        else reject(new Error(result.error?.message ?? "Outlook couldn't open the meeting form"));
      });
    });
  }
  mailbox.displayNewAppointmentForm(params);
  return Promise.resolve();
}

/**
 * Outlook on the web's new-event page, filled in: the last resort, for Outlook
 * versions with no form the pane can open, or when Outlook's form couldn't
 * open. Must run from a click, or within seconds of one: browsers block windows opened any other way.
 */
export function openOutlookWebCompose(draft: NewMeetingDraft): void {
  const params = new URLSearchParams({
    rru: "addevent",
    subject: draft.subject,
    startdt: draft.start.toISOString(),
    enddt: draft.end.toISOString(),
  });
  if (draft.attendees.length > 0) params.set("to", draft.attendees.join(";"));
  if (draft.location) params.set("location", draft.location);
  if (draft.bodyText) params.set("body", draft.bodyText);
  const url = `https://outlook.office.com/calendar/action/compose?${params.toString()}`;
  openInBrowser(url, "calendar-open-new-meeting-web");
}
